/**
 * The landing ring's rules (ADR-095 decision 20 and note 51): which bursts have one, how its one texture is painted
 * and at what alpha it shows. Under a shot whose burst is `RING_MIN_SIXTEENTHS` or wider lies a line `RING_LINE_PIXELS`
 * wide whose outer edge is the burst's radius, around a faint disc, in the `danger` role, the line at `RING_ALPHA` and
 * the disc at `RING_DISC_ALPHA`. The projectile view paints the texture once an ability and places it.
 */

/**
 * The least burst radius that is given a landing ring, in sixteenths of a tile.
 */
export const RING_MIN_SIXTEENTHS = 12;

/**
 * The alpha of the landing ring's line.
 */
export const RING_ALPHA = 0.4;

/**
 * The alpha of the disc the landing ring's line encloses, faint enough that the ground under it still reads.
 */
export const RING_DISC_ALPHA = 0.12;

/**
 * How wide the landing ring's line is, in world pixels, measured inward from the burst's radius.
 */
export const RING_LINE_PIXELS = 2;

/**
 * The texture key of a landing ring of a radius.
 *
 * @param radius The radius in world pixels.
 * @returns The key.
 */
export function ringTextureKey(radius: number): string {
  return `projectile:ring:${radius}`;
}

/**
 * Whether a pixel of a ring's texture is on the ring's line: `RING_LINE_PIXELS` wide around the centre pixel, its outer
 * edge at the radius, so the line marks the area that harms and never reaches past it.
 *
 * @param x The pixel's column.
 * @param y The pixel's row.
 * @param radius The radius in pixels; the texture is `2 * radius + 1` across.
 * @returns True on the line.
 */
export function onRing(x: number, y: number, radius: number): boolean {
  const distance = Math.hypot(x - radius, y - radius);
  return distance > radius + 0.5 - RING_LINE_PIXELS && distance <= radius + 0.5;
}

/**
 * The alpha a pixel of a ring's texture is painted at, before the ring's sprite is drawn at `RING_ALPHA`: 1 on the
 * line, the disc's share within it, so the disc shows at `RING_DISC_ALPHA`, and 0 outside the radius.
 *
 * @param x The pixel's column.
 * @param y The pixel's row.
 * @param radius The radius in pixels; the texture is `2 * radius + 1` across.
 * @returns The alpha, 0 to 1.
 */
export function ringPixelAlpha(x: number, y: number, radius: number): number {
  if (onRing(x, y, radius)) {
    return 1;
  }
  return Math.hypot(x - radius, y - radius) <= radius ? RING_DISC_ALPHA / RING_ALPHA : 0;
}
